import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress, Grid2, Typography } from "@mui/material";
import { DialogEx } from "@/lib/components/DialogEx";
import { ButtonEx } from "@/lib/components/buttons/ButtonEx";
import SelectInputField from "@/lib/components/inputfields/select-input-field";
import { AddressDTO, Country, LegalForm } from "@/api/data-contracts";
import { CommonI18N, CompanyI18N } from "@/translations";
import { allowedLegalForms, toSortedLegalFormOptions } from "@/model/legalForm";
import { DataCacheStatus } from "@/lib/datacache";

export type LegalFormForCountryDialogProps = {
    open: boolean;
    newCountry: Country | undefined;
    // Structural subset instead of the full AddressDTO: only the display fields are needed, so the
    // dialog works for both the company-page AddressDTO and the admin-page AdminAddressDTO.
    address?: Pick<AddressDTO, "street" | "houseNumber" | "zip" | "city">;
    legalFormsByCountry: Record<string, LegalForm[]>;
    legalFormsByCountryStatus: DataCacheStatus;
    // True while the caller's combined save is in flight — shows a busy indicator on the Save
    // button and suppresses double submits (the server-side geocoding call can take a while).
    saving?: boolean;
    onConfirm: (legalForm: LegalForm) => void;
    onCancel: () => void;
};

/**
 * Follow-up dialog shown when the operator changes the company address's country to one that is
 * incompatible with the currently stored legal form: offers a select of the legal forms allowed for the
 * new country and reports the operator's choice via onConfirm. This component makes no persistence
 * decision itself — the caller decides what to do with the confirmed legal form (e.g. call the combined
 * address+legal-form update endpoint atomically) or with a cancellation (save nothing).
 */
export const LegalFormForCountryDialog = ({
    open,
    newCountry,
    address,
    legalFormsByCountry,
    legalFormsByCountryStatus,
    saving = false,
    onConfirm,
    onCancel
}: LegalFormForCountryDialogProps) => {
    const { t } = useTranslation();
    const [legalForm, setLegalForm] = useState<LegalForm | "">("");

    // Reset the selection whenever the dialog (re-)opens — otherwise a previous choice would linger
    // across an intermediate cancel or a country change while the component stays mounted.
    useEffect(() => {
        if (open) {
            setLegalForm("");
        }
    }, [open, newCountry]);

    const legalFormSelectOptions = useMemo(
        () => toSortedLegalFormOptions(allowedLegalForms(newCountry, legalFormsByCountry), t),
        [newCountry, legalFormsByCountry, t]
    );

    // Defense-in-depth: the caller (AddressEditDialog) already gates opening this dialog on the reference
    // cache being fully loaded, but a still-loading cache would otherwise render this dialog with an empty
    // options list and a permanently disabled Save button (UI dead-end) — guard here too so the component
    // stays safe to use on its own.
    const isReferenceDataLoaded = legalFormsByCountryStatus === "loaded";

    const handleConfirm = () => {
        if (legalForm && !saving) {
            onConfirm(legalForm);
        }
    };

    const buttons = (
        <>
            <Grid2>
                <ButtonEx label={t(CommonI18N.action.cancel)} color="secondary" disabled={saving} onClick={onCancel} />
            </Grid2>
            <Grid2>
                <ButtonEx
                    label={(saving && <CircularProgress size={24} />) || t(CompanyI18N.form.action.save)}
                    color="primary"
                    disabled={!legalForm || !isReferenceDataLoaded || saving}
                    onClick={handleConfirm}
                />
            </Grid2>
        </>
    );

    return (
        <DialogEx
            title={t(CompanyI18N.form.dialog.legalFormForCountryTitle)}
            open={open}
            onClose={onCancel}
            maxWidth="sm"
            fullWidth
            buttons={buttons}
        >
            <Grid2 container spacing={2} padding={2} width="100%">
                <Grid2 size={{ xs: 12 }}>
                    <Typography>{t(CompanyI18N.form.dialog.legalFormForCountryMessage)}</Typography>
                </Grid2>
                {address && (
                    <Grid2 size={{ xs: 12 }}>
                        <Typography variant="body2" color="textSecondary">
                            {address.street} {address.houseNumber}, {address.zip} {address.city}
                        </Typography>
                    </Grid2>
                )}
                <Grid2 size={{ xs: 12 }}>
                    <SelectInputField
                        name="legalForm"
                        label={t(CompanyI18N.form.field.legalForm)}
                        value={legalForm}
                        options={legalFormSelectOptions}
                        displayEmpty={true}
                        onChange={(e) => setLegalForm(e.target.value as LegalForm)}
                    />
                </Grid2>
            </Grid2>
        </DialogEx>
    );
};
